import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import colors from '../theme/colors';
import LoginModal from './LoginModal';

const LoginContainer = styled(motion.div)`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  
  /* Mobile: relative positioning when inside ButtonsContainer */
  @media (max-width: 768px) {
    position: static;
  }
`;

const LoginButton = styled(motion.button)`
  background: ${colors.base.themePrimary};
  border: 1px solid ${colors.border.light};
  border-radius: 12px;
  color: ${colors.text.light};
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: ${colors.effects.shadow};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.button.aboutBgHover};
    border-color: ${colors.border.lightHover};
    transform: translateY(-2px);
    box-shadow: ${colors.effects.shadowHover};
  }
`;

const UserProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 15px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const UserPicture = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const LogoutButton = styled(motion.button)`
  background: ${colors.button.adminActiveBg};
  border: none;
  border-radius: 8px;
  color: ${colors.button.adminText};
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${colors.border.light};

  &:hover {
    background: ${colors.button.adminActiveBgHover};
    transform: translateY(-1px);
  }
`;

const AuthButton = () => {
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { t } = useTranslation();
  const [showLoginModal, setShowLoginModal] = useState(false);

  if (loading) {
    return null;
  }

  if (isAuthenticated) {
    return (
      <LoginContainer>
        <UserProfile id="user-profile">
          {user.pictureUrl && <UserPicture src={user.pictureUrl} alt="User Avatar" />}
          <LogoutButton onClick={logout}>
            {t('navigation.logout')}
          </LogoutButton>
        </UserProfile>
      </LoginContainer>
    );
  }

  return (
    <LoginContainer id="auth-login-container">
      <LoginButton
        onClick={() => setShowLoginModal(true)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        {t('auth.loginButton')}
      </LoginButton>
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
    </LoginContainer>
  );
};

export default AuthButton;